<template>
  <div class="page">
    <div class="card">
      <div class="topbar">
        <div>
          <h1 class="title">Painel do administrador</h1>
          <p class="subtitle">Consulte relatorios e edite dados dos colaboradores.</p>
        </div>
        <div class="topbar-right">
          <span class="pill">Administrador</span>
          <button class="btn btn-ghost" type="button" @click="goToUser">Submissoes</button>
          <button class="btn btn-ghost" type="button" @click="logout">Sair</button>
        </div>
      </div>

      <div class="tabs">
        <button class="tab" :class="{ active: activeTab === 'submissions' }" @click="activeTab = 'submissions'">
          Submissoes
        </button>
        <button class="tab" :class="{ active: activeTab === 'reports' }" @click="activeTab = 'reports'">
          Consulta de documentos
        </button>
        <button class="tab" :class="{ active: activeTab === 'safety' }" @click="activeTab = 'safety'">
          Seguranca do trabalho
        </button>
        <button class="tab" :class="{ active: activeTab === 'users' }" @click="activeTab = 'users'">
          Usuarios
        </button>
      </div>

      <section v-if="activeTab === 'submissions'">
        <div class="section-header" style="margin-bottom: 12px;">
          <div class="subtitle">Todas as submissoes registradas.</div>
        </div>
        <div v-if="items.length === 0" class="item-card">
          <h3 class="item-title">Nenhuma submissao registrada</h3>
          <p class="item-meta">Crie um novo relatorio ou aguarde os colaboradores.</p>
        </div>
        <div v-else class="grid card-list">
          <div v-for="item in items" :key="item.id" class="item-card">
            <div>
              <h3 class="item-title">
                {{ item.title }} - {{ item.attraction }} - {{ formatReportDate(item.reportDate) }}
              </h3>
              <div class="item-meta">
                {{ formatDate(item.createdAt) }} - {{ item.author.name }}
              </div>
            </div>
            <div class="actions">
              <button class="btn btn-secondary" type="button" @click="view(item.id)">Visualizar</button>
              <button class="btn btn-ghost" type="button" @click="edit(item.id)">Editar</button>
              <button class="btn btn-accent" type="button" @click="remove(item.id)">Excluir</button>
            </div>
          </div>
        </div>
      </section>

      <section v-else-if="activeTab === 'reports'">
        <div v-if="selectedAuthor || selectedType" class="item-card" style="margin-bottom: 14px;">
          <div class="section-header" style="margin-bottom: 0;">
            <div>
              <strong>Filtro ativo:</strong>
              <span v-if="selectedAuthor">{{ selectedAuthor }}</span>
              <span v-if="selectedAuthor && selectedType"> - </span>
              <span v-if="selectedType">{{ selectedType }}</span>
            </div>
            <div class="actions">
              <button class="btn btn-ghost" type="button" @click="clearFilters">Limpar filtro</button>
            </div>
          </div>
        </div>
        <div v-else class="section-header" style="margin-bottom: 12px;">
          <div class="subtitle">Consulta de documentos por colaborador.</div>
          <div class="actions">
            <select class="tab" v-model="selectedType">
              <option value="">Todos os tipos</option>
              <option v-for="item in documentTypes" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
        </div>
        <div v-if="grouped.length === 0" class="item-card">
          <h3 class="item-title">Sem documentos para consulta</h3>
          <p class="item-meta">Os documentos enviados pelos colaboradores aparecerao aqui.</p>
        </div>

        <div v-else class="grid" style="gap: 20px;">
          <div v-for="group in grouped" :key="group.author" class="item-card">
            <div>
              <h3 class="item-title">{{ group.author }}</h3>
              <div class="item-meta">{{ group.items.length }} submissao(oes)</div>
            </div>
            <div class="grid" style="gap: 10px;">
              <div v-for="item in group.items" :key="item.id" class="item-card" style="padding: 12px;">
                <div>
                  <div class="item-title" style="font-size: 15px;">
                    {{ item.title }} - {{ item.attraction }} - {{ formatReportDate(item.reportDate) }}
                  </div>
                  <div class="item-meta">{{ formatDate(item.createdAt) }} - {{ item.status }}</div>
                </div>
                <div class="actions">
                  <button class="btn btn-secondary" type="button" @click="view(item.id)">Preview</button>
                  <button class="btn btn-ghost" type="button" @click="edit(item.id)">Editar</button>
                </div>
              </div>
            </div>
            <div class="actions">
              <button class="btn btn-primary" type="button" @click="exportDoc(group.author)">
                Montar Word
              </button>
            </div>
          </div>
        </div>
      </section>
      <section v-else-if="activeTab === 'safety'">
        <div class="section-header" style="margin-bottom: 12px;">
          <div class="subtitle">Submissoes de seguranca do trabalho.</div>
        </div>
        <div v-if="safetyItems.length === 0" class="item-card">
          <h3 class="item-title">Nenhuma submissao de seguranca</h3>
          <p class="item-meta">Aguarde novos documentos ou atualize.</p>
        </div>
        <div v-else class="grid card-list">
          <div v-for="item in safetyItems" :key="item.id" class="item-card">
            <div>
              <h3 class="item-title">
                {{ item.title }} - {{ item.attraction }} - {{ formatReportDate(item.reportDate) }}
              </h3>
              <div class="item-meta">
                {{ formatDate(item.createdAt) }} - {{ item.author.name }}
              </div>
            </div>
            <div class="actions">
              <button class="btn btn-secondary" type="button" @click="view(item.id)">Visualizar</button>
              <button class="btn btn-ghost" type="button" @click="edit(item.id)">Editar</button>
              <button class="btn btn-accent" type="button" @click="remove(item.id)">Excluir</button>
            </div>
          </div>
        </div>
      </section>

      <section v-else>
        <div class="grid" style="gap: 18px;">
          <div class="item-card">
            <div class="section-header" style="margin-bottom: 10px;">
              <h3 class="item-title">Usuarios cadastrados</h3>
              <div class="actions">
                <button class="icon-btn" type="button" @click="openAccountForm">+</button>
              </div>
            </div>
            <div v-if="accounts.length === 0" class="item-meta">Nenhum usuario encontrado.</div>
            <div v-else class="grid" style="gap: 12px; margin-top: 12px;">
              <div v-for="acc in accounts" :key="acc.username" class="list-row">
                <div>
                  <strong>{{ acc.name }}</strong>
                  <div class="item-meta">{{ acc.username }} - {{ acc.role }}</div>
                </div>
                <div class="actions">
                  <button class="btn btn-secondary" type="button" @click="editAccount(acc)">Editar</button>
                  <button class="btn btn-ghost" type="button" @click="viewUserSubmissions(acc.name)">
                    Ver submissoes
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showAccountForm" class="item-card">
            <h3 class="item-title">{{ accountForm.isEditing ? 'Editar conta' : 'Nova conta' }}</h3>
            <div class="form-grid" style="margin-top: 12px;">
              <div class="input-field">
                <label for="acc-name">Nome</label>
                <input id="acc-name" v-model="accountForm.name" type="text" placeholder="Nome completo" />
              </div>
              <div class="input-field">
                <label for="acc-username">Username</label>
                <input id="acc-username" v-model="accountForm.username" type="text" placeholder="usuario" />
              </div>
              <div class="input-field">
                <label for="acc-birth">Data de nascimento <span v-if="accountForm.isEditing">(em branco mantém)</span></label>
                <input id="acc-birth" v-model="accountForm.birthDate" type="date" :min="MIN_BIRTH_DATE" :max="maxBirthDate()" />
              </div>
              <div class="input-field">
                <label for="acc-password">Password</label>
                <input id="acc-password" v-model="accountForm.password" type="text" placeholder="senha" />
              </div>
              <div class="input-field">
                <label for="acc-role">Role</label>
                <select id="acc-role" v-model="accountForm.role">
                  <option value="user">Usuario</option>
                  <option value="admin-obra">Administrador</option>
                </select>
              </div>
              <div class="actions">
                <button class="btn btn-primary" type="button" @click="saveAccount">
                  {{ accountForm.isEditing ? 'Atualizar' : 'Criar' }}
                </button>
                <button class="btn btn-ghost" type="button" @click="closeAccountForm">Fechar</button>
              </div>
              <p v-if="accountError" class="subtitle" style="color: #a54b2a;">{{ accountError }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { submissionsStore } from '@/store/submissions'
import { sessionStore } from '@/store/session'
import { accountsStore, type Account, type AccountRole } from '@/store/accounts'
import { rolesStore } from '@/store/roles'
import { birthDateError, maxBirthDate, MIN_BIRTH_DATE } from '@/utils/birthDate'
import { formatDateOnly, formatDateTime } from '@/utils/date'
import { uiStore } from '@/store/ui'

export default defineComponent({
  name: 'AdminReportsView',
  setup() {
    const router = useRouter()
    const activeTab = ref<'submissions' | 'reports' | 'safety' | 'users'>('submissions')

    const safetyTypes = [
      'Dialogos de seguranca',
      'Permissao de trabalho critico',
      'Relatorio de ocorrido'
    ]

    const reportTypes = ['Relatorio diario de obra', 'Relatorio de incidente']
    const documentTypes = [...reportTypes, ...safetyTypes]

    const items = computed(() => submissionsStore.all())

    const selectedAuthor = ref<string | null>(null)

    const selectedType = ref('')

    const grouped = computed(() => {
      const map = new Map<string, typeof items.value>()
      const filtered = items.value.filter((item) => {
        if (selectedType.value && item.title !== selectedType.value) return false
        return true
      })
      filtered.forEach((item) => {
        const list = map.get(item.author.name) || []
        list.push(item)
        map.set(item.author.name, list)
      })
      const entries = Array.from(map.entries()).map(([author, list]) => ({
        author,
        items: list
      }))
      if (selectedAuthor.value) {
        return entries.filter((entry) => entry.author === selectedAuthor.value)
      }
      return entries
    })

    const safetyItems = computed(() =>
      items.value.filter((item) => safetyTypes.includes(item.title))
    )

    const view = (id: string) => {
      const item = submissionsStore.getById(id)
      if (item?.category === 'relatorio') {
        router.push(`/submission/${id}`)
        return
      }
      router.push(`/safety/${id}`)
    }
    const edit = (id: string) => {
      const item = submissionsStore.getById(id)
      if (item?.category === 'dialogo') {
        router.push(`/safety/dialogo/${id}`)
        return
      }
      if (item?.category === 'ocorrido') {
        router.push(`/safety/ocorrido/${id}`)
        return
      }
      if (item?.category === 'permissao') {
        router.push('/forgot')
        return
      }
      router.push(`/submission/${id}/edit`)
    }
    const remove = async (id: string) => {
      if (!await uiStore.confirm('Excluir esta submissao?')) return
      await submissionsStore.deleteSubmission(id)
    }

    const exportDoc = (author: string) => {
      const list = submissionsStore.byAuthor(author)
      const rows = list
        .map((item) => {
          const collaborators = item.collaborators.join(', ') || '—'
          const tools = item.tools.map(t => t.text).filter(Boolean).join(', ') || '—'
          const observations = item.observations.map(o => o.text).filter(Boolean).join(', ') || '—'
          const activities = item.activities
            .map((activity, index) => {
              const images = activity.images
                .map(
                  (img) =>
                    `<img src="${img}" style="width: 120px; height: 120px; object-fit: cover; margin: 4px;" />`
                )
                .join('')
              return `
                <div style="margin-bottom: 8px;">
                  <strong>Atividade ${index + 1}:</strong> ${activity.description || '—'}
                  <div style="margin-top: 6px;">${images || 'Sem imagens'}</div>
                </div>
              `
            })
            .join('')

          return `
            <tr><th colspan="2" style="text-align: left; background: #f4f4f4;">Relatorio</th></tr>
            <tr><td style="width: 220px;"><strong>Tipo</strong></td><td>${item.title}</td></tr>
            <tr><td><strong>Atracao</strong></td><td>${item.attraction}</td></tr>
            <tr><td><strong>Data</strong></td><td>${item.reportDate}</td></tr>
            <tr><td><strong>Status</strong></td><td>${item.status}</td></tr>
            <tr><td><strong>Criado em</strong></td><td>${formatDateTime(item.createdAt)}</td></tr>
            <tr><td><strong>Colaboradores</strong></td><td>${collaborators}</td></tr>
            <tr><td><strong>Ferramentas</strong></td><td>${tools}</td></tr>
            <tr><td><strong>Observacoes</strong></td><td>${observations}</td></tr>
            <tr><td><strong>Atividades</strong></td><td>${activities || 'Sem atividades'}</td></tr>
            <tr><td colspan="2" style="height: 10px; background: #fff;"></td></tr>
          `
        })
        .join('')
      const html = `
        <html>
          <head><meta charset="utf-8" /></head>
          <body>
            <h2>Relatorios de ${author}</h2>
            <table border="1" cellspacing="0" cellpadding="6" style="width: 100%; border-collapse: collapse;">
              ${rows}
            </table>
          </body>
        </html>
      `
      const blob = new Blob(['\ufeff', html], { type: 'application/msword' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `relatorios-${author.replace(/\s+/g, '-').toLowerCase()}.doc`
      link.click()
      window.URL.revokeObjectURL(url)
    }

    const logout = () => {
      sessionStore.logout()
      router.push('/login')
    }

    const goToUser = () => router.push('/submissions')

    const accounts = computed(() => accountsStore.all())
    const accountError = ref('')
    const showAccountForm = ref(false)
    const accountForm = ref({
      uid: '',
      name: '',
      username: '',
      password: '',
      birthDate: '',
      role: 'user' as AccountRole,
      roleId: '',
      subordinateUsernames: [] as string[],
      isEditing: false
    })

    const resetAccountForm = () => {
      accountForm.value = {
        uid: '',
        name: '',
        username: '',
        password: '',
        birthDate: '',
        role: 'user',
        roleId: rolesStore.all().find((role) => role.name.toLowerCase() === 'usuario')?.id || '',
        subordinateUsernames: [],
        isEditing: false
      }
      accountError.value = ''
    }

    const openAccountForm = () => {
      resetAccountForm()
      showAccountForm.value = true
    }

    const closeAccountForm = () => {
      showAccountForm.value = false
      resetAccountForm()
    }

    const editAccount = (account: Account) => {
      accountForm.value = {
        uid: account.uid,
        name: account.name,
        username: account.username,
        password: '',
        birthDate: '',
        role: account.role,
        roleId: account.roleId || '',
        subordinateUsernames: account.subordinateUsernames || [],
        isEditing: true
      }
      accountError.value = ''
      showAccountForm.value = true
    }

    const saveAccount = async () => {
      accountError.value = ''
      if (!accountForm.value.name.trim() || !accountForm.value.username.trim()) {
        accountError.value = 'Preencha nome e username.'
        return
      }
      if (!accountForm.value.isEditing && !accountForm.value.password.trim()) {
        accountError.value = 'Informe uma senha para criar a conta.'
        return
      }
      if (!accountForm.value.isEditing || accountForm.value.birthDate) {
        const birthErr = birthDateError(accountForm.value.birthDate)
        if (birthErr) { accountError.value = birthErr; return }
      }
      try {
        await accountsStore.upsert({
          uid: accountForm.value.uid,
          name: accountForm.value.name.trim(),
          username: accountForm.value.username.trim(),
          password: accountForm.value.password.trim() || undefined,
          role: accountForm.value.role,
          roleId: accountForm.value.roleId,
          subordinateUsernames: accountForm.value.subordinateUsernames,
          hasSubordinates: (accountForm.value.subordinateUsernames || []).length > 0,
          avatar: '',
          birthDate: accountForm.value.birthDate
        })
        closeAccountForm()
      } catch (err: unknown) {
        accountError.value = err instanceof Error ? err.message : 'Falha ao salvar.'
      }
    }

    const viewUserSubmissions = (authorName: string) => {
      selectedAuthor.value = authorName
      selectedType.value = ''
      activeTab.value = 'reports'
    }

    const clearFilters = () => {
      selectedAuthor.value = null
      selectedType.value = ''
    }

    const refreshSubmissions = () => {
      submissionsStore.refresh().catch(() => {
        // ignore for now
      })
    }

    const refreshAccounts = () => {
      accountsStore.refresh().catch(() => {
        accountError.value = 'Falha ao carregar contas.'
      })
    }

    onMounted(() => {
      refreshSubmissions()
      refreshAccounts()
    })

    return {
      MIN_BIRTH_DATE, maxBirthDate,
      activeTab,
      items,
      grouped,
      selectedAuthor,
      selectedType,
      documentTypes,
      safetyItems,
      formatDate: formatDateTime,
      formatReportDate: formatDateOnly,
      view,
      edit,
      remove,
      exportDoc,
      logout,
      goToUser,
      accounts,
      accountForm,
      accountError,
      showAccountForm,
      resetAccountForm,
      openAccountForm,
      closeAccountForm,
      editAccount,
      saveAccount,
      viewUserSubmissions,
      clearFilters,
      refreshSubmissions,
      refreshAccounts
    }
  }
})
</script>
